import React from 'react';
import { FileText } from 'lucide-react';
import type { Contribution } from '../../types/campaignFinance';
import { fmtMoney, fmtDate, getContributorDisplayName } from '../../utils/campaignFinanceUtils';
import Chip from '../Chip';
import CommitteeLink from '../committee/CommitteeLink';
import Button from '../Button';

interface CFDetailHeroProps {
  contribution: Contribution;
  onViewRaw: () => void;
  cycleSummary?: {
    this_gift_rank?: number;
    this_pct_of_cycle?: number;
    largest_gift?: number;
    median_gift?: number;
  };
}

function rankSuffix(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n}st`;
  if (mod10 === 2 && mod100 !== 12) return `${n}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${n}rd`;
  return `${n}th`;
}

function getTypeChipVariant(type?: string): 'success' | 'warning' | 'neutral' {
  if (!type) return 'neutral';
  const lower = type.toLowerCase();
  if (lower.includes('monetary')) return 'success';
  if (lower.includes('in-kind')) return 'warning';
  return 'neutral';
}

const SVG_ACCENT = 'var(--cf-bar)';   // primary-400
const SVG_PRIMARY = 'var(--cf-out-deep)';  // primary-800

const PartyCard: React.FC<{
  role: string;
  name: React.ReactNode;
  sub?: string;
  party?: string | null;
  accentColor: string;
}> = ({ role, name, sub, party, accentColor }) => (
  <div className="card p-0 overflow-hidden">
    <div className="flex">
      <div className="w-1.5 flex-shrink-0" style={{ backgroundColor: accentColor }} />
      <div className="p-4 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">{role}</span>
          {party && (
            <Chip
              label={party}
              variant={
                party.toLowerCase().includes('democrat') ? 'info' :
                party.toLowerCase().includes('republican') ? 'danger' :
                'neutral'
              }
              size="xs"
            />
          )}
        </div>
        <div className="text-base font-semibold text-text-primary dark:text-text-dark-primary">{name}</div>
        {sub && <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-0.5">{sub}</div>}
      </div>
    </div>
  </div>
);

const CFDetailHero: React.FC<CFDetailHeroProps> = ({ contribution, onViewRaw, cycleSummary }) => {
  const contributorName = getContributorDisplayName(contribution);

  return (
    <div className="card p-0 overflow-hidden">
      {/* Top band */}
      <div className="bg-background-tertiary dark:bg-background-dark-tertiary px-7 py-5 border-b border-border-light dark:border-border-dark">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-2">
              Campaign Finance Record
            </div>
            <div className="text-4xl font-bold text-text-primary dark:text-text-dark-primary tabular-nums leading-none">
              {fmtMoney(contribution.contribution_amount)}
            </div>
            <div className="flex items-center gap-2 mt-2">
              {contribution.contribution_type && (
                <Chip label={contribution.contribution_type} variant={getTypeChipVariant(contribution.contribution_type)} size="sm" />
              )}
              {contribution.contributor_type && (
                <Chip label={contribution.contributor_type} variant="primary" size="sm" />
              )}
              {contribution.committee_type && (
                <Chip label={contribution.committee_type} variant="info" size="sm" />
              )}
              {contribution.amended && (
                <Chip label="Amended" variant="warning" size="sm" />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {cycleSummary && (
              <div className="hidden md:grid grid-cols-2 gap-3 mr-4">
                {cycleSummary.this_gift_rank && (
                  <div className="text-center">
                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary">Rank</div>
                    <div className="text-lg font-bold text-text-primary dark:text-text-dark-primary tabular-nums">
                      {rankSuffix(cycleSummary.this_gift_rank)}
                    </div>
                  </div>
                )}
                {cycleSummary.this_pct_of_cycle !== undefined && (
                  <div className="text-center">
                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary">Share</div>
                    <div className="text-lg font-bold text-text-primary dark:text-text-dark-primary tabular-nums">
                      {(cycleSummary.this_pct_of_cycle * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
                {cycleSummary.largest_gift !== undefined && (
                  <div className="text-center">
                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary">Largest</div>
                    <div className="text-lg font-bold text-text-primary dark:text-text-dark-primary tabular-nums">
                      {fmtMoney(cycleSummary.largest_gift, { compact: true })}
                    </div>
                  </div>
                )}
                {cycleSummary.median_gift !== undefined && (
                  <div className="text-center">
                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary">Median</div>
                    <div className="text-lg font-bold text-text-primary dark:text-text-dark-primary tabular-nums">
                      {fmtMoney(cycleSummary.median_gift, { compact: true })}
                    </div>
                  </div>
                )}
              </div>
            )}
            <Button variant="outline" size="sm" icon={FileText} onClick={onViewRaw}>
              View full record
            </Button>
          </div>
        </div>
      </div>

      {/* From / To row */}
      <div className="px-7 py-5">
        <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-center">
          <PartyCard
            role="From"
            name={contributorName}
            sub={[contribution.occupation, contribution.employer].filter(Boolean).join(' at ') || contribution.city ? `${contribution.city}, ${contribution.state}` : undefined}
            party={null}
            accentColor={SVG_ACCENT}
          />
          <div className="hidden md:flex flex-col items-center">
            <svg width="60" height="60" viewBox="0 0 60 60">
              <line x1="8" y1="30" x2="44" y2="30" stroke={SVG_ACCENT} strokeWidth="2" strokeDasharray="4 3" />
              <path d="M42 24 L52 30 L42 36" fill="none" stroke={SVG_PRIMARY} strokeWidth="2" />
              <circle cx="8" cy="30" r="4" fill={SVG_PRIMARY} />
            </svg>
            <span className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
              {fmtDate(contribution.contribution_date)}
            </span>
          </div>
          <PartyCard
            role="To"
            name={contribution.committee_name
              ? <CommitteeLink coId={contribution.co_id} name={contribution.committee_name} className="text-primary-700 dark:text-primary-dark-400" />
              : 'Unknown Committee'}
            sub={contribution.candidate_name || undefined}
            party={contribution.candidate_party || null}
            accentColor={SVG_PRIMARY}
          />
        </div>
      </div>
    </div>
  );
};

export default CFDetailHero;
